/**
 * Trace Explorer page.
 *
 * URL: ``/trace/:anchorType/:anchorId``
 *
 * Walks the causal graph from the URL-encoded anchor and renders it
 * as a left-to-right flow: upstream (received/triggered) on the left,
 * the anchor in the centre, downstream (emitted/chained) on the right.
 *
 * Depth controls (back / forward) live in the page header so the
 * operator can widen the view when ``truncated_*`` is set. The right
 * sidebar shows the currently-selected node with a deep-link back to
 * the entity's primary admin page.
 */
import { useQuery } from "@tanstack/react-query";
import { GitBranch, RefreshCw, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { TraceCanvas } from "@/components/trace/TraceCanvas";
import { TraceSidebar } from "@/components/trace/TraceSidebar";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { traceApi, type TraceQuery } from "@/lib/api-trace";
import { cn } from "@/lib/cn";
import { useMediaQuery } from "@/lib/useMediaQuery";


// Same closed vocabulary as the backend Literal.
const ANCHOR_TYPES = ["message", "execution", "node_execution", "delivery"] as const;
type AnchorType = (typeof ANCHOR_TYPES)[number];

function isAnchorType(v: string | undefined): v is AnchorType {
  return !!v && (ANCHOR_TYPES as readonly string[]).includes(v);
}


export function TracePage() {
  const { t } = useTranslation();
  const params = useParams<{ anchorType: string; anchorId: string }>();
  const navigate = useNavigate();
  // The operator wants to see the WHOLE causal graph, always — no
  // manual back/forward stepping. We request the safety cap (10) in
  // both directions; for these graphs that's effectively "everything".
  const FULL_DEPTH = 10;
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  // Validate up-front: a bad URL is much easier to recover from with a
  // friendly empty-state than a confusing API error. Hooks below must
  // still run unconditionally — we just disable the query when the URL
  // is invalid so the empty-state renders cleanly without a fetch.
  const valid = isAnchorType(params.anchorType) && !!params.anchorId;

  const queryArgs: TraceQuery = {
    anchor_type: (valid ? params.anchorType : "message") as TraceQuery["anchor_type"],
    anchor_id: params.anchorId ?? "",
    back_depth: FULL_DEPTH,
    forward_depth: FULL_DEPTH,
  };

  const query = useQuery({
    queryKey: ["trace", queryArgs],
    queryFn: () => traceApi.get(queryArgs),
    enabled: valid,
  });

  // Quick stats line under the title — operators want to see the
  // shape of the graph (size + failures) without scanning the canvas.
  // Hoisted above the invalid-URL early-return to keep hook order stable.
  const stats = React.useMemo(() => {
    if (!query.data) return null;
    const failed = query.data.nodes.filter(
      (n) => (n.data as { status?: string }).status === "failed",
    ).length;
    return {
      nodes: query.data.nodes.length,
      edges: query.data.edges.length,
      failed,
    };
  }, [query.data]);

  // Viewport switch for the description string (long-with-UUID on
  // tablet/desktop, short-counts-only on phone). MUST be called
  // unconditionally — hoisted above the early-return like ``stats``.
  const isWide = useMediaQuery("(min-width: 640px)");

  if (!valid) {
    return (
      <PageContainer>
        <PageContent>
          <EmptyState
            icon={GitBranch}
            title={t("trace.invalid.title")}
            description={t("trace.invalid.description")}
          />
        </PageContent>
      </PageContainer>
    );
  }

  // The synthetic anchor id used in the graph mirrors the backend's
  // ``"<kind>:<uuid>"`` convention.
  const anchorGraphId = `${params.anchorType}:${params.anchorId}`;
  const selectedNode =
    query.data?.nodes.find((n) => n.id === selectedId) ??
    // Desktop only: when nothing is explicitly selected, default to
    // showing the anchor in the side rail (always visible on md+).
    // On mobile the panel is an overlay — defaulting to anchor would
    // open it on first load AND make the X button useless (clicking
    // it sets selectedId=null which fell back to anchor again).
    (isWide && selectedId === null
      ? query.data?.nodes.find((n) => n.id === anchorGraphId) ?? null
      : null);

  // Description: keep it short on mobile (the long UUID makes the
  // narrow flex column wrap character-by-character on phone widths).
  // Full anchor info is reachable from the URL and from the sidebar.
  // ``stats.*`` uses i18next pluralization via the ``_one`` / ``_other``
  // suffixes — Italian and English happen to share the same rule but
  // wiring it now means future languages drop in cleanly.
  const statsSummary = stats
    ? [
        t("trace.stats.nodes", { count: stats.nodes }),
        t("trace.stats.edges", { count: stats.edges }),
        ...(stats.failed > 0 ? [t("trace.stats.failed", { count: stats.failed })] : []),
      ].join(" · ")
    : "";
  const anchorLabel = `${t("trace.anchor")}: ${params.anchorType}`;
  const shortDescription = stats ? statsSummary : params.anchorType ?? "";
  const longDescription = stats
    ? `${anchorLabel} · ${params.anchorId} · ${statsSummary}`
    : `${anchorLabel} · ${params.anchorId}`;
  const description = isWide ? longDescription : shortDescription;

  return (
    <PageContainer>
      <PageHeader
        title={t("trace.title")}
        description={description}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
            >
              <RefreshCw
                className={cn("h-3.5 w-3.5", query.isFetching && "animate-spin")}
                strokeWidth={1.5}
              />
              {/* Hide the label on phone widths so the actions row
                  doesn't squeeze the title column. */}
              <span className="hidden sm:inline">{t("common.refresh")}</span>
            </Button>
          </div>
        }
      />
      <PageContent className="!p-0">
        {query.isLoading ? (
          <div className="grid h-full place-items-center text-sm text-muted-foreground">
            {t("common.loading")}
          </div>
        ) : query.isError ? (
          <EmptyState
            icon={GitBranch}
            title={t("trace.error.title")}
            description={(query.error as Error).message}
          />
        ) : !query.data || query.data.nodes.length === 0 ? (
          <EmptyState
            icon={GitBranch}
            title={t("trace.empty.title")}
            description={t("trace.empty.description")}
          />
        ) : (
          <div className="flex h-full">
            <div className="relative flex-1">
              {/* We always fetch the full graph (depth cap 10 both
                  ways), so a truncation banner would only appear on a
                  pathologically deep chain — and there's no manual
                  "expand" anymore. Kept as a passive notice. */}
              {(query.data.truncated_back || query.data.truncated_forward) && (
                <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded border border-amber-400/40 bg-amber-400/10 px-2 py-1 text-[11px] text-amber-300">
                  {t("trace.truncated.deep")}
                </div>
              )}
              <TraceCanvas
                nodes={query.data.nodes}
                edges={query.data.edges}
                anchorId={anchorGraphId}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            </div>
            {/* Responsive sidebar: at md+ it's a 340px right rail;
                below md it collapses to a bottom overlay that only
                appears when the user has selected a node. The overlay
                has its own close affordance so dismissing it on touch
                doesn't require clicking the (sometimes-hidden) canvas
                pane. */}
            <aside
              className={cn(
                "border-border transition-transform",
                // Desktop: side rail, always visible, semi-transparent
                // fill is fine because it sits next to the canvas, not
                // on top of it.
                "md:block md:w-[340px] md:shrink-0 md:border-l md:relative md:max-h-none md:bg-surface/30",
                // Mobile: hidden when no selection, slide-up bottom
                // overlay when something is selected. Solid background
                // (was bg-surface/30 → canvas bled through and made
                // the panel content unreadable on touch).
                selectedNode
                  ? "fixed inset-x-0 bottom-0 z-30 block max-h-[65vh] border-t bg-background shadow-2xl md:static md:shadow-none md:bg-surface/30"
                  : "hidden",
              )}
            >
              {/* Mobile-only close button — invisible on md+ where the
                  aside is permanent. */}
              {selectedNode && (
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  aria-label={t("trace.closeDetails")}
                  className="absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-elevated hover:text-foreground md:hidden"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              )}
              <TraceSidebar
                node={selectedNode ?? null}
                onTrace={(anchor) => {
                  // Pivot the page to the chosen node: navigates to a
                  // fresh trace URL with the same depths. The query
                  // key changes so React Query refetches automatically.
                  setSelectedId(null);
                  navigate(`/trace/${anchor.type}/${anchor.id}`);
                }}
              />
            </aside>
          </div>
        )}
      </PageContent>
    </PageContainer>
  );
}


